import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { all, dropTask, restartableTask } from 'ember-concurrency';
import { and, not, or } from 'ember-truth-helpers';
import ConfirmDirtyTransition from 'garaje/components/confirm-dirty-transition';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type LocationModel from 'garaje/models/location';
import type VisualComplianceConfigurationModel from 'garaje/models/visual-compliance-configuration';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type ImpressionsService from 'garaje/services/impressions';
import type MetricsService from 'garaje/services/metrics';
import type { AnySubscriptionModel } from 'garaje/services/state';
import type StateService from 'garaje/services/state';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';

const integration_name = 'Visual Compliance';
const message_code_type = 'integration';

interface VisualCompliancePanelComponentSignature {
  Args: {
    isOpen: boolean;
    location?: LocationModel;
    subscription?: AnySubscriptionModel; // Used by template
    visualComplianceConfiguration?: VisualComplianceConfigurationModel;
    onCreateConfiguration: (config: VisualComplianceConfigurationModel) => void;
    onUpgrade?: () => void;
    toggle: (isOpened: boolean) => void;
  };
  Blocks: {
    default: [];
  };
}

export default class VisualCompliancePanelComponent extends Component<VisualCompliancePanelComponentSignature> {
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare impressions: ImpressionsService;
  @service declare metrics: MetricsService;
  @service declare store: Store;
  @service declare state: StateService;

  @tracked confirmUninstall = false;
  @tracked configuration: VisualComplianceConfigurationModel | null;

  constructor(owner: unknown, args: VisualCompliancePanelComponentSignature['Args']) {
    super(owner, args);
    this.configuration = this.args.visualComplianceConfiguration || null;
  }

  get location(): LocationModel | null {
    return this.args.location || null;
  }

  get hasAccessToVisualCompliance(): boolean {
    return !!this.state.features?.canAccessVisualCompliance;
  }

  get isPanelOpen(): boolean {
    return !!(this.args.location?.visualComplianceEnabled && this.configuration && this.args.isOpen);
  }

  onCloseVisualCompliance = (): void => {
    this.args.toggle(false);
    this.metrics.trackJobEvent('Security - Integration Section Close Button Clicked', {
      integration_name,
      button_text: 'X',
    });
  };

  onClickConfigure = (): void => {
    const { configuration } = this;

    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Go to Modify Integration Settings Button Clicked', {
      integration_name,
      button_text: 'Configure',
      credentials_status: configuration?.credentialsInvalid && configuration?.secno ? 'invalid' : 'valid',
    });
    this.args.toggle(true);
  };

  onClickUpgrade = (): void => {
    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Integration Upgrade Button Clicked', {
      integration_name,
      button_text: 'Upgrade',
      plan_upgrade_target: 'enterprise',
    });
    this.args.onUpgrade?.();
  };

  installTask = dropTask(async () => {
    const { location } = this;

    if (!location) return;

    this.metrics.startJob();
    this.metrics.trackJobEvent('Security - Go to Integration Section Button Clicked', {
      integration_name,
      button_text: 'Install',
    });

    try {
      if (!this.configuration) {
        const config = this.store.createRecord('visual-compliance-configuration', { location });

        await config.save();

        this.configuration = config;
        this.args.onCreateConfiguration(config);
      }

      location.visualComplianceEnabled = true;

      this.metrics.trackJobEvent('Security - Integration Install Requested', { integration_name });

      await location.save();

      this.metrics.trackJobEvent('Security - Integration Installed', { integration_name });

      this.args.toggle(true);

      this.flashMessages.showAndHideFlash('success', 'Installed!');

      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'success',
        integration_name,
        message_title: 'Installed!',
        message_code: 'integration_install_success',
        message_code_type,
      });

      await this.impressions.postImpression.perform(
        IMPRESSION_NAMES.SECURITY_VR_SCREENING_OPTIONS_VISUAL_COMPLIANCE['ENABLED'],
      );
    } catch (e) {
      location.rollbackAttributes();
      this.configuration?.rollbackAttributes?.();
      this.args.toggle(false);

      const message_title = 'There was an issue installing Visual Compliance';
      const message_code = parseErrorForDisplay(e);

      this.flashMessages.showFlash('error', message_title, message_code);
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'error',
        integration_name,
        message_title,
        message_code,
        message_code_type,
      });
    }
  });

  uninstallTask = dropTask(async () => {
    const { location } = this;

    if (!location) return;

    this.confirmUninstall = false;
    this.metrics.trackJobEvent(`Security - Integration Uninstall Button Clicked`, {
      integration_name,
      button_text: 'Uninstall',
    });

    // Only reset local (not Global) visualComplianceConfigurations
    const flows = location.flows.content.filter((flow) => !flow.isGlobalChild);
    let flowConfigurations = await all(flows.map((flow) => flow.visualComplianceConfiguration));
    flowConfigurations = flowConfigurations.filter(Boolean);

    flowConfigurations.map((config) => (config.fieldsToCheck = ['name']));
    location.visualComplianceEnabled = false;
    this.args.toggle(false);

    try {
      this.metrics.trackJobEvent('Security - Integration Uninstall Requested', { integration_name });

      await all([location, ...flowConfigurations].map((record) => record.save()));

      this.metrics.trackJobEvent('Security - Integration Uninstalled', { integration_name });
      this.flashMessages.showAndHideFlash('success', 'Uninstalled!');
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'success',
        integration_name,
        message_title: 'Uninstalled!',
        message_code: 'integration_uninstall_success',
        message_code_type,
      });

      await this.impressions.postImpression.perform(
        IMPRESSION_NAMES.SECURITY_VR_SCREENING_OPTIONS_VISUAL_COMPLIANCE['DISABLED'],
      );
    } catch (e) {
      location.rollbackAttributes();
      flowConfigurations.map((config) => config.rollbackAttributes());
      this.args.toggle(true);

      const message_title = 'There was an issue uninstalling Visual Compliance';
      const message_code = parseErrorForDisplay(e);

      this.flashMessages.showAndHideFlash('error', message_title, message_code);
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'error',
        integration_name,
        message_title,
        message_code,
        message_code_type,
      });
    }
  });

  saveTask = restartableTask(async () => {
    const { configuration } = this;

    if (!configuration) return;

    try {
      this.metrics.trackJobEvent('Security - Integration Settings Update Requested', { integration_name });

      await configuration.save();

      this.metrics.trackJobEvent('Security - Integration Settings Updated', {
        integration_name,
        visual_compliance_contact_ids: configuration.contactIds,
        visual_compliance_contacts_modified: true,
      });

      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'success',
        integration_name,
        message_title: 'Saved!',
        message_code: 'integration_update_success',
        message_code_type,
      });
    } catch (e) {
      const message_title = 'Error';
      const message_code = parseErrorForDisplay(e);

      this.flashMessages.showFlash('error', message_title, message_code);
      this.metrics.trackJobEvent('Viewed Flash Message', {
        type: 'error',
        integration_name,
        message_title,
        message_code,
        message_code_type,
      });
    }
  });

  <template>
    <SettingsPanel
      @isOpen={{this.isPanelOpen}}
      @onCancel={{this.onCloseVisualCompliance}}
      data-test-visual-compliance-panel
      as |sp|
    >
      {{#let sp.header sp.body sp.footer sp.isOpen as |PanelHeader PanelBody PanelFooter PanelIsOpen|}}
        <PanelHeader
          @iconUri="/assets/images/integrations/visual-compliance.png"
          @title="Visual Compliance"
          @description="Check if visitors appear on third-party lists of restricted parties."
          @pillText="Integration"
          as |header|
        >
          <header.controls>
            {{#if (and this.hasAccessToVisualCompliance (not PanelIsOpen))}}
              {{#if (and @location.visualComplianceEnabled this.installTask.isIdle)}}
                <button
                  data-test-visual-compliance-configure
                  type="button"
                  class="btn btn--small btn--brand"
                  {{on "click" this.onClickConfigure}}
                >
                  {{if this.configuration.credentialsInvalid "Configure" "Edit"}}
                </button>
              {{else}}
                <button
                  data-test-visual-compliance-install
                  type="button"
                  class="btn btn--small btn--brand"
                  aria-disabled={{this.installTask.isRunning}}
                  disabled={{this.installTask.isRunning}}
                  {{on "click" this.installTask.perform}}
                >
                  {{#if this.installTask.isRunning}}
                    <img class="pl-2 pr-2" src="/assets/images/progress-small-white.svg" alt="Installing..." />
                  {{else}}
                    Install
                  {{/if}}
                </button>
              {{/if}}
            {{else if (not PanelIsOpen)}}
              <button
                type="button"
                class="btn btn--small btn--brand"
                {{on "click" this.onClickUpgrade}}
                data-test-visual-compliance-upgrade-button
              >
                Upgrade
              </button>
            {{else if (and PanelIsOpen (not this.configuration.credentialsInvalid))}}
              <button
                data-test-visual-compliance-save
                type="button"
                class="btn btn--small btn--brand"
                disabled={{or this.saveTask.isRunning (not this.configuration.hasDirtyAttributes)}}
                {{on "click" this.saveTask.perform}}
              >
                {{if this.saveTask.isRunning "Saving..." "Save"}}
              </button>
            {{/if}}
          </header.controls>
        </PanelHeader>
        <PanelBody as |PanelContent|>
          <PanelContent @style="inset">
            {{yield}}
          </PanelContent>
          <PanelFooter>
            {{#if @location.visualComplianceEnabled}}
              <button
                data-test-visual-compliance-uninstall
                type="button"
                class="btn btn--small btn--outlineFill"
                {{on "click" (fn (mut this.confirmUninstall) true)}}
              >
                Uninstall
              </button>
            {{/if}}
          </PanelFooter>
        </PanelBody>
      {{/let}}
    </SettingsPanel>

    {{#if this.confirmUninstall}}
      <ConfirmDirtyTransition
        @headerMessage="Uninstall Visual Compliance?"
        @bodyMessage="This will reset any configurations made to the Visual Compliance integration in Envoy for this location."
        @confirmText="Uninstall"
        @confirm={{this.uninstallTask.perform}}
        @cancel={{fn (mut this.confirmUninstall) false}}
      />
    {{/if}}
  </template>
}
